import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, App, Card, Input, Modal, Descriptions, Tag, Divider, Tooltip, Typography, Flex } from 'antd';
import { MailOutlined, ReloadOutlined, SearchOutlined, CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, SendOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/appState';
import { makeRequest } from '../../../helpers/axios-manipulator';
import dayjs from 'dayjs';

const Orders: React.FC = () => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const user = useSelector((state: RootState) => state.user.user);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await makeRequest({
        origin: window.location.origin,
        method: 'get',
        url: `/kpiData?rootUser=true&userId=${user.rep_id || user.id || ''}&contract_id=${(user as any).contract_id ?? ''}`,
      });
      setOrders(Array.isArray(response?.orders) ? response.orders : []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      message.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [user]);

  const handleResendReceipt = async (orderId: string, targets: string = 'all', e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      const response = await makeRequest({
        origin: window.location.origin,
        method: 'post',
        url: '/api/admin/resend-receipt',
        data: { orderId, targets }
      });
      if (response.success) {
        message.success(response.message || 'Requested emails resent successfully');
      } else {
        message.error(response.error || 'Failed to resend emails');
      }
    } catch (err: any) {
      console.error('Resend receipt error:', err);
      message.error(err.response?.data?.error || 'Internal server error');
    }
  };

  const validateOrder = (order: any) => {
    if (!order.order_amount) return { valid: false, reason: 'Missing order amount' };
    
    const totalAmount = parseFloat(order.order_amount);
    const artworkPrice = parseFloat(order.artwork_price || order.final_price || totalAmount);
    
    // Business Criteria: Platform Fee = 7% + $5 (calculated on Artwork Price)
    const expectedFees = Math.round(artworkPrice * 0.07 * 100 + 500) / 100;
    const actualFees = parseFloat(order.fees || 0);
    
    // Check if fees match (within $0.05 tolerance for rounding)
    const feesValid = Math.abs(expectedFees - actualFees) < 0.05;
    
    // Stripe Fees: calculated on the TOTAL amount paid by collector
    const stripeFees = parseFloat(order.stripe_fees || (totalAmount * 0.029 + 0.30));
    
    // Rep Payouts: can be multiple, separated by ;
    const repPayouts = String(order.rep_payout || '').split(';').filter(Boolean).map(v => parseFloat(v) || 0);
    const totalRepPayout = repPayouts.reduce((a, b) => a + b, 0);

    // Ujamaa Rep Fees: Platform's take from the rep commission
    const ujamaaRepFees = String(order.ujamaa_rep_fees || '').split(';').filter(Boolean).map(v => parseFloat(v) || 0);
    const totalUjamaaRepFees = ujamaaRepFees.reduce((a, b) => a + b, 0);
    
    // Artist Payout = Artwork Price - Platform Fees - Stripe Fees - totalRepPayout - totalUjamaaRepFees
    const expectedPayout = artworkPrice - actualFees - stripeFees - totalRepPayout - totalUjamaaRepFees;
    const actualPayout = parseFloat(order.artist_payout || 0);
    
    const payoutValid = !isNaN(expectedPayout) && Math.abs(expectedPayout - actualPayout) < 0.15; 

    if (!feesValid) return { valid: false, reason: `Platform fee mismatch. Expected ~$${expectedFees.toFixed(2)} (based on $${artworkPrice}), got $${actualFees.toFixed(2)}` };
    if (!payoutValid) return { valid: false, reason: `Artist payout mismatch. Expected ~$${expectedPayout.toFixed(2)}, got $${actualPayout.toFixed(2)}` };
    
    return { valid: true, reason: 'Follows all business criteria' };
  };

  const filteredOrders = useMemo(() => {
    let result = [...orders];
    
    // Sort by ID desc
    result.sort((a, b) => {
      const idA = parseInt(a.id) || 0;
      const idB = parseInt(b.id) || 0;
      return idB - idA;
    });

    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      result = result.filter(o => 
        (o.id && o.id.toString().toLowerCase().includes(lowerSearch)) ||
        (o.artist_username && o.artist_username.toLowerCase().includes(lowerSearch)) ||
        (o.owner_full_name && o.owner_full_name.toLowerCase().includes(lowerSearch)) ||
        (o.collector_username && o.collector_username.toLowerCase().includes(lowerSearch)) ||
        (o.stripe_trx_id && o.stripe_trx_id.toLowerCase().includes(lowerSearch)) ||
        (o.rep_usernames && o.rep_usernames.toLowerCase().includes(lowerSearch))
      );
    }
    return result;
  }, [orders, searchText]);

  const showDetails = (order: any) => {
    setSelectedOrder(order);
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: any, b: any) => (parseInt(a.id) || 0) - (parseInt(b.id) || 0),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Date',
      dataIndex: 'order_date',
      key: 'order_date',
      render: (val: any) => val ? dayjs(val).format('MMM D, YYYY h:mm A') : '',
    },
    {
      title: 'Artist',
      dataIndex: 'artist_username',
      key: 'artist_username',
    },
    {
      title: 'Collector',
      key: 'collector',
      render: (row: any) => row.owner_full_name || row.collector_username || '',
    },
    {
      title: 'Amount',
      dataIndex: 'order_amount',
      key: 'order_amount',
      render: (val: any) => val ? `$${parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '$0.00',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let color = 'default';
        if (status === 'completed' || status === 'paid') color = 'success';
        if (status === 'pending') color = 'processing';
        if (status === 'failed') color = 'error';
        return <Tag color={color}>{status?.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Validation',
      key: 'validation',
      render: (_: any, record: any) => {
        const { valid, reason } = validateOrder(record);
        return (
          <Tooltip title={reason}>
            {valid ? (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            ) : (
              <CloseCircleOutlined style={{ color: '#f5222d' }} />
            )}
          </Tooltip>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <Button 
          type="link" 
          icon={<MailOutlined />} 
          onClick={(e) => handleResendReceipt(record.id, 'all', e)}
        >
          Resend Email
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card 
        title={
          <Flex align="center" gap={16}>
            <span>Order Management</span>
            <Input
              placeholder="Search by ID, Artist, or Collector"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
          </Flex>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchOrders} loading={loading}>
            Refresh
          </Button>
        }
      >
        <Table 
          dataSource={filteredOrders} 
          columns={columns} 
          loading={loading} 
          rowKey="id"
          pagination={{ pageSize: 20 }}
          onRow={(record) => ({
            onClick: () => showDetails(record),
            style: { cursor: 'pointer' }
          })}
        />
      </Card>

      <Modal
        title={`Order Details: #${selectedOrder?.id}`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedOrder && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Order ID">{selectedOrder.id}</Descriptions.Item>
              <Descriptions.Item label="Order Date">{dayjs(selectedOrder.order_date).format('MMM D, YYYY h:mm A')}</Descriptions.Item>
              <Descriptions.Item label="Artist">{selectedOrder.artist_username}</Descriptions.Item>
              <Descriptions.Item label="Collector">
                <Flex vertical gap={0}>
                  <span>{selectedOrder.owner_full_name || selectedOrder.collector_username}</span>
                  <span style={{ fontSize: '12px', color: '#8c8c8c' }}>{selectedOrder.owner_email}</span>
                  {selectedOrder.collector_username === 'Checkout as Guest' && (
                    <Tag color="orange" style={{ marginTop: 4 }}>Guest Checkout</Tag>
                  )}
                </Flex>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={selectedOrder.status === 'completed' || selectedOrder.status === 'paid' ? 'success' : 'processing'}>
                  {selectedOrder.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Stripe Transaction ID">{selectedOrder.stripe_trx_id || 'N/A'}</Descriptions.Item>
            </Descriptions>

            <Divider>Financial Breakdown</Divider>
            {(() => {
              const artworkPrice = parseFloat(selectedOrder.artwork_price || selectedOrder.final_price || selectedOrder.order_amount || 0);
              const fee7Percent = Math.round(artworkPrice * 0.07 * 100) / 100;
              const feeFixed = 5.00;
              const stripeFees = parseFloat(selectedOrder.stripe_fees || 0);
              const repPayouts = String(selectedOrder.rep_payout || '').split(';').filter(Boolean).map(v => parseFloat(v) || 0);
              const totalRepPayout = repPayouts.reduce((a, b) => a + b, 0);
              const ujamaaRepFees = String(selectedOrder.ujamaa_rep_fees || '').split(';').filter(Boolean).map(v => parseFloat(v) || 0);
              const totalUjamaaRepFees = ujamaaRepFees.reduce((a, b) => a + b, 0);
              const totalFees = parseFloat(selectedOrder.fees || 0);
              const artistPayout = parseFloat(selectedOrder.artist_payout || 0);

              const ujamaaBalance = totalFees + totalUjamaaRepFees;

              return (
                <>
                  <Descriptions bordered column={2}>
                    <Descriptions.Item label="Collector Paid (Total)" span={2}>
                      <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>${parseFloat(selectedOrder.order_amount || 0).toFixed(2)}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Artwork Price">${artworkPrice.toFixed(2)}</Descriptions.Item>
                    <Descriptions.Item label="Stripe Processing Fees">-${stripeFees.toFixed(2)}</Descriptions.Item>
                    
                    <Descriptions.Item label="Platform Fee (7%)">-${fee7Percent.toFixed(2)}</Descriptions.Item>
                    <Descriptions.Item label="Transfer Fee">-$5.00</Descriptions.Item>
                    
                    {selectedOrder.rep_id && (
                      <Descriptions.Item label="Representative Split" span={2}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {String(selectedOrder.rep_id).split(';').filter(Boolean).map((id, idx) => {
                            const username = String(selectedOrder.rep_usernames || '').split(';')[idx]?.trim() || id.trim();
                            const percentage = String(selectedOrder.rep_commission || '').split(';')[idx]?.trim() || '0';
                            const payout = String(selectedOrder.rep_payout || '').split(';')[idx]?.trim() || '0';
                            const platformFee = String(selectedOrder.ujamaa_rep_fees || '').split(';')[idx]?.trim() || '0';
                            
                            return (
                              <div key={id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: '#f9f9f9', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
                                <Flex gap="small">
                                  <Tag color="blue">@{username}</Tag>
                                  <span style={{ fontSize: '12px' }}>{percentage}% Commission</span>
                                </Flex>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontSize: '13px' }}>Payout: <span style={{ fontWeight: 'bold' }}>-${parseFloat(payout).toFixed(2)}</span></div>
                                  <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Platform Cut: -${parseFloat(platformFee).toFixed(2)}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  <Divider style={{ margin: '16px 0' }}>Final Balances</Divider>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                    <Card size="small" style={{ textAlign: 'center', background: '#f6ffed', borderColor: '#b7eb8f' }}>
                      <div style={{ color: '#52c41a', fontSize: '12px' }}>ARTIST BALANCE</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#389e0d' }}>${artistPayout.toFixed(2)}</div>
                    </Card>
                    <Card size="small" style={{ textAlign: 'center', background: '#e6f7ff', borderColor: '#91d5ff' }}>
                      <div style={{ color: '#1890ff', fontSize: '12px' }}>REP BALANCE</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#096dd9' }}>${totalRepPayout.toFixed(2)}</div>
                    </Card>
                    <Card size="small" style={{ textAlign: 'center', background: '#fff7e6', borderColor: '#ffd591' }}>
                      <div style={{ color: '#fa8c16', fontSize: '12px' }}>UJAMAA BALANCE</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#d46b08' }}>${ujamaaBalance.toFixed(2)}</div>
                    </Card>
                  </div>
                </>
              );
            })()}


            <div style={{ marginTop: 16 }}>
              <Flex gap="middle" align="center">
                <span style={{ fontWeight: 'bold' }}>Business Validation:</span>
                {(() => {
                  const { valid, reason } = validateOrder(selectedOrder);
                  return (
                    <Tag color={valid ? 'success' : 'error'} icon={valid ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                      {reason}
                    </Tag>
                  );
                })()}
              </Flex>
            </div>
            <Divider>Communication & Actions</Divider>
            <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
              <Typography.Paragraph strong>Resend Notifications</Typography.Paragraph>
              <Flex wrap="wrap" gap="small">
                <Button 
                  icon={<MailOutlined />} 
                  onClick={() => handleResendReceipt(selectedOrder.id, 'collector')}
                >
                  Resend to Collector
                </Button>
                <Button 
                  icon={<MailOutlined />} 
                  onClick={() => handleResendReceipt(selectedOrder.id, 'artist')}
                >
                  Resend to Artist
                </Button>
                {selectedOrder.rep_id && (
                  <Button 
                    icon={<MailOutlined />} 
                    onClick={() => handleResendReceipt(selectedOrder.id, 'reps')}
                  >
                    Resend to Representatives
                  </Button>
                )}
                <Button 
                  type="primary"
                  icon={<SendOutlined />} 
                  onClick={() => handleResendReceipt(selectedOrder.id, 'all')}
                >
                  Resend to All Parties
                </Button>
              </Flex>
            </div>

            {selectedOrder.stripe_receipt_url && (
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Button type="link" href={selectedOrder.stripe_receipt_url} target="_blank">
                  View Stripe Receipt
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default Orders;

